import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { AuthState } from "./auth";

export interface PaymentItemState {
  id: string;
  datetime_created: string;
  datetime_updated: string | null;
  created_by: string;
  last_updated_by: string;
  request_option: [string, string];
  category: [string, string];
  description: string;
  price: string;
  tax_percentage: string;
  image: string;
  status: [string, string];
}

// Define a type for the slice state
export interface PaymentState {
  items: {
    data: PaymentItemState[] | null;
    loading: boolean;
    waiting: boolean;
    errors: string[];
  };
  options: {
    [key: string]: any;
  };
}

// Define the initial state using that type
const initialState: PaymentState = {
  items: {
    data: null,
    loading: false,
    waiting: false,
    errors: [],
  },
  options: {},
};

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

// Get payment items
export const getPaymentItems = createAsyncThunk(
  "payment/items/get",
  async (
    obj: {
      category: string | null;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build endpoint URL
      let url = `/api/payment/item/`;
      if (obj.category !== null) url += `?category=${obj.category}`;

      // Send request
      const res = await axios.get(url, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Create payment item
export const createPaymentItem = createAsyncThunk(
  "payment/item/post",
  async (
    obj: {
      property: string;
      requestOption: string;
      description: string | null;
      price: string;
      taxPercentage: string;
      image: { id: any; source: string; data: any } | null;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        property: obj.property,
        request_option: obj.requestOption,
        description: obj.description,
        status: obj.status,
        price: obj.price,
        tax_percentage: obj.taxPercentage,
        image: obj.image,
      };

      // Build URL
      const url = "/api/payment/item/";

      // Send request
      const res = await axios.post(url, body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Update payment item
export const updatePaymentItem = createAsyncThunk(
  "payment/item/put",
  async (
    obj: {
      paymentItemId: string;
      property: string;
      requestOption: string;
      description: string | null;
      price: string;
      taxPercentage: string;
      image: { id: any; source: string; data: any } | null;
      status: string;
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token
      const config = { headers: { Authorization: `Bearer ${auth.access}` } };

      // Build body
      const body = {
        id: obj.paymentItemId,
        property: obj.property,
        request_option: obj.requestOption,
        description: obj.description,
        status: obj.status,
        price: obj.price,
        tax_percentage: obj.taxPercentage,
        image: obj.image,
      };
      const res = await axios.put("/api/payment/item/", body, config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

// Delete payment items
export const deletePaymentItems = createAsyncThunk(
  "payment/items/delete",
  async (
    obj: {
      paymentItemIds: string[];
    },
    { getState, rejectWithValue },
  ) => {
    try {
      // Get user data from store
      const { auth } = getState() as { auth: AuthState };

      // Configure authorization header with user's token and data
      const config = {
        headers: {
          Authorization: `Bearer ${auth.access}`,
          "Content-Type": "application/json",
        },
        data: { id: obj.paymentItemIds },
      };

      const res = await axios.delete("/api/payment/item/", config);
      return { data: res.data, status: res.status };
    } catch (err: any) {
      if (err.response && err.response.data.message) {
        return rejectWithValue(err.response.data.message);
      } else {
        return rejectWithValue(err.message);
      }
    }
  },
);

//////////////////////////////
//////// Async Thunks ////////
//////////////////////////////

/////////////////////////////
//////// Redux slice ////////
/////////////////////////////

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Get payment items
      .addCase(getPaymentItems.pending, (state) => {
        state.items.loading = true;
        state.items.waiting = false;
      })
      .addCase(getPaymentItems.fulfilled, (state, action) => {
        state.items.data = action.payload.data.data;
        state.options.approved_request_options =
          action.payload.data.options.approved_request_options;
        state.options.remaining_request_options =
          action.payload.data.options.remaining_request_options;
        state.options.category = action.payload.data.options.category;
        state.items.loading = false;
        state.items.waiting = false;
      })
      .addCase(getPaymentItems.rejected, (state) => {
        state.items.loading = false;
        state.items.waiting = false;
      })

      // Create payment item
      .addCase(createPaymentItem.pending, (state) => {
        state.items.loading = true;
        state.items.waiting = false;
      })
      .addCase(createPaymentItem.fulfilled, (state, action) => {
        state.items.data =
          state.items.data === null
            ? [action.payload.data]
            : [action.payload.data, ...state.items.data];
        state.items.loading = false;
        state.items.waiting = false;
      })
      .addCase(createPaymentItem.rejected, (state) => {
        state.items.loading = false;
        state.items.waiting = false;
      })

      // Update payment item
      .addCase(updatePaymentItem.pending, (state) => {
        state.items.waiting = true;
        state.items.loading = true;
      })
      .addCase(updatePaymentItem.fulfilled, (state, action) => {
        const previousData = state.items.data === null ? [] : state.items.data;
        const newData = [];
        for (let i = 0; i < previousData.length; i++) {
          const item = previousData[i];
          if (item.id === action.payload.data.id) {
            newData.push(action.payload.data);
          } else {
            newData.push(item);
          }
        }
        state.items.data = newData;
        state.items.loading = false;
        state.items.waiting = false;
      })
      .addCase(updatePaymentItem.rejected, (state) => {
        state.items.waiting = false;
        state.items.loading = false;
      })

      // Delete payment items
      .addCase(deletePaymentItems.pending, (state) => {
        state.items.loading = true;
        state.items.waiting = false;
      })
      .addCase(deletePaymentItems.fulfilled, (state, action) => {
        const existingData = state.items.data === null ? [] : state.items.data;
        const updatedData = existingData.filter(
          (item) => !action.payload.data.id.includes(item.id),
        );
        state.items.data = updatedData;
        state.items.loading = false;
        state.items.waiting = false;
      })
      .addCase(deletePaymentItems.rejected, (state) => {
        state.items.loading = false;
        state.items.waiting = false;
      });
  },
});

export default paymentSlice.reducer;
